import type { OverviewLocation } from '@/domain/client';

/** The parts of a client-overview location the cohort name is read from. */
export interface PenCohortSource {
    cages: Array<{
        cageId: number;
        activeFishGroup?: { id: number; name: string } | null;
        /** Set on a pen marked historical, which predates fish groups and has none. */
        historicalSince?: string | null;
    }>;
    closedFishGroups?: OverviewLocation['closedFishGroups'];
}

/**
 * The name of the cohort a pen's breadcrumb should mention.
 *
 * Resolved from the client overview because the breadcrumb lives in `AppShell`,
 * above the cage providers, so it cannot read the window the cage pages resolved.
 * The two agree by construction: both answer "which fish group is this pen's data
 * about", and both prefer the id in the URL over the pen's current occupant.
 *
 * `selectedFishGroupId` is the `fg` URL param. It takes precedence precisely
 * because it is the only way to name a *closed* cohort — the pen itself only
 * knows who is in it now, so without it a page showing last season's batch would
 * carry this season's name in the breadcrumb.
 *
 * Returns undefined when there is nothing trustworthy to show: no cohort data
 * requested (flag off), a pen with no group, or an id that belongs to another pen.
 * Naming the wrong cohort is worse than naming none.
 */
export function resolvePenCohortName(
    location: PenCohortSource | null | undefined,
    cageId: number | undefined,
    selectedFishGroupId: number | undefined
): string | undefined {
    if (!location || cageId == null) {
        return undefined;
    }

    const cage = location.cages.find((candidate) => candidate.cageId === cageId);

    if (selectedFishGroupId != null) {
        if (cage?.activeFishGroup?.id === selectedFishGroupId) {
            return cage.activeFishGroup.name;
        }

        // A closed stay is looked up by pen as well as by group: the same group may
        // have lived in several pens, and only the stay in *this* one is ours to name.
        return location.closedFishGroups?.find(
            (stay) => stay.cageId === cageId && stay.fishGroupId === selectedFishGroupId
        )?.fishGroupName;
    }

    return cage?.activeFishGroup?.name ?? undefined;
}

/**
 * The fish group a pen's data requests should be scoped to: the `fg` URL param, else the group
 * in the pen now. `undefined` for an empty pen or with no cohort data (flag off), which leaves a
 * request cage-wide, as before fish groups.
 *
 * Unlike the name, an `fg` that is not this pen's is returned as is: the backend owns that check
 * and answers it with a 404, as it does for every other request carrying the id.
 */
export function resolvePenCohortFishGroupId(
    location: PenCohortSource | null | undefined,
    cageId: number | undefined,
    selectedFishGroupId: number | undefined
): number | undefined {
    if (selectedFishGroupId != null) {
        return selectedFishGroupId;
    }
    const cage = location?.cages.find((candidate) => candidate.cageId === cageId);
    return cage?.activeFishGroup?.id ?? undefined;
}

/**
 * What a pen page has to do with its `fg` before showing anything, with fish groups on.
 *
 * - `keep`: `fg` names this pen's active group or one of its closed stays, a historical pen is
 *   reached without `fg` (its data is the cage's, from before fish groups), or the pen is not in
 *   the overview (the pen pages own that not-found).
 * - `select`: no `fg`, and the pen has a group now. The page is about that group, so the URL
 *   names it; without `fg` every request would go out from the cage's point of view.
 * - `leave`: no group to show. An empty pen that is not historical reached without `fg`, or an
 *   `fg` that is not this pen's, which would otherwise answer with another pen's data or a 404
 *   on every request.
 *
 * Same validity rule as `resolvePenCohortName`, so a page the guard keeps is one whose cohort
 * the breadcrumb and title can name.
 */
export type PenFishGroupDecision =
    | { kind: 'keep' }
    | { kind: 'select'; fishGroupId: number }
    | { kind: 'leave' };

export function resolvePenFishGroupDecision(
    location: PenCohortSource,
    cageId: number,
    selectedFishGroupId: number | undefined
): PenFishGroupDecision {
    const cage = location.cages.find((candidate) => candidate.cageId === cageId);
    if (!cage) {
        return { kind: 'keep' };
    }

    if (selectedFishGroupId == null) {
        const activeId = cage.activeFishGroup?.id;
        if (activeId != null) {
            return { kind: 'select', fishGroupId: activeId };
        }
        return cage.historicalSince != null ? { kind: 'keep' } : { kind: 'leave' };
    }

    const isActive = cage.activeFishGroup?.id === selectedFishGroupId;
    const isClosedStay = location.closedFishGroups?.some(
        (stay) => stay.cageId === cageId && stay.fishGroupId === selectedFishGroupId
    );
    return isActive || isClosedStay ? { kind: 'keep' } : { kind: 'leave' };
}
